import React from "react";

/**
 * DegreeRing — the brand's gold degree symbol with hot-air fumes.
 * Reusable: sits in the hero a° mark and inline in headlines.
 * Gradient sampled from the original logo; steam drifts through a
 * turbulence filter like real heat off a loaf just out of the oven.
 */
export const DegreeRing = ({ size = "1em", withSteam = true, hover = true, display = "inline-block", className = "" }) => {
  const uid = React.useId().replace(/[^a-zA-Z0-9]/g, "");
  const goldId = `hoaGold${uid}`;
  const steamGradId = `hoaSteamGrad${uid}`;
  const turbId = `hoaSteamTurb${uid}`;
  return (
  <span
    aria-hidden="true"
    className={`relative ${className}`}
    style={{ width: size, height: size, display }}
  >
    <svg viewBox="0 0 40 40" className={`block w-full h-full ${hover ? "hoa-ring-hover" : ""}`}>
      <defs>
        <linearGradient id={goldId} x1="0" y1="0" x2="0.35" y2="1">
          <stop offset="0%" stopColor="#D2B770" />
          <stop offset="52%" stopColor="#B3914A" />
          <stop offset="100%" stopColor="#886A33" />
        </linearGradient>
      </defs>
      <circle cx="20" cy="20" r="15" fill="none" stroke={`url(#${goldId})`} strokeWidth="6" />
    </svg>

    {withSteam && (
      <svg
        viewBox="0 0 60 60"
        data-testid="degree-steam"
        className="absolute overflow-visible pointer-events-none"
        style={{
          bottom: "100%",
          left: "50%",
          transform: "translateX(-50%) translateZ(0)",
          width: "172.5%",
          height: "142.5%",
        }}
      >
        <defs>
          <linearGradient id={steamGradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F0E9D8" stopOpacity="0" />
            <stop offset="45%" stopColor="#F0E9D8" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#F0E9D8" stopOpacity="0.6" />
          </linearGradient>
          <filter id={turbId} x="-80%" y="-40%" width="260%" height="200%">
            <feTurbulence type="fractalNoise" baseFrequency="0.04 0.09" numOctaves="2" seed="7" result="turb" />
            <feDisplacementMap in="SourceGraphic" in2="turb" scale="9" xChannelSelector="R" yChannelSelector="G" />
            <feGaussianBlur stdDeviation="0.5" />
          </filter>
        </defs>
        <g filter={`url(#${turbId})`}>
          <g className="steam-sway s1">
            <path
              className="steam-rise"
              d="M20 54 C17 47, 23 41, 20 34 C17 27, 22 21, 20 15"
              fill="none"
              stroke={`url(#${steamGradId})`}
              strokeWidth="2.4"
              strokeLinecap="round"
            />
          </g>
          <g className="steam-sway s2">
            <path
              className="steam-rise"
              d="M30 56 C33 47, 26 40, 30 30 C34 21, 27 12, 30 4"
              fill="none"
              stroke={`url(#${steamGradId})`}
              strokeWidth="3"
              strokeLinecap="round"
            />
          </g>
          <g className="steam-sway s3">
            <path
              className="steam-rise"
              d="M40 54 C37 48, 43 41, 40 33 C37 26, 42 20, 40 13"
              fill="none"
              stroke={`url(#${steamGradId})`}
              strokeWidth="2.4"
              strokeLinecap="round"
            />
          </g>
          <g className="steam-sway s4">
            <path
              className="steam-rise"
              d="M12 52 C10 46, 14 41, 12 35 C10 29, 13 24, 12 19"
              fill="none"
              stroke={`url(#${steamGradId})`}
              strokeWidth="2"
              strokeLinecap="round"
            />
          </g>
          <g className="steam-sway s5">
            <path
              className="steam-rise"
              d="M48 52 C46 47, 50 42, 48 36 C46 30, 49 25, 48 20"
              fill="none"
              stroke={`url(#${steamGradId})`}
              strokeWidth="2"
              strokeLinecap="round"
            />
          </g>
        </g>
      </svg>
    )}
  </span>
  );
};

/* The brand "a" — exact vector path extracted from the uploaded logo PDF */
export const A_PATH =
  "M56.7 225.54 C39.62 225.54 25.9 220.92 15.54 211.68 C5.18 202.44 0.0 190.26 0.0 175.14 C0.0 168.7 1.12 162.61 3.36 156.87 C5.6 151.13 9.8 145.46 15.96 139.86 C22.4 134.26 31.22 128.45 42.42 122.43 C53.62 116.41 68.46 110.04 86.94 103.32 L126.0 89.04 L126.0 57.54 C126.0 41.58 123.48 28.91 118.44 19.53 C113.4 10.15 106.4 5.46 97.44 5.46 C86.8 5.74 77.77 12.74 70.35 26.46 C62.93 40.18 57.96 59.22 55.44 83.58 L10.08 57.54 C14.84 46.06 22.12 35.98 31.92 27.3 C41.72 18.62 52.99 11.9 65.73 7.14 C78.47 2.38 91.84 0.0 105.84 0.0 C129.64 0.0 149.03 6.65 164.01 19.95 C178.99 33.25 186.48 51.38 186.48 74.34 L186.48 175.56 C186.48 191.8 191.94 199.92 202.86 199.92 C209.3 199.92 215.6 196.42 221.76 189.42 L225.12 192.36 C217.84 202.16 209.02 210.14 198.66 216.3 C188.3 222.46 178.22 225.54 168.42 225.54 C157.78 225.54 148.61 221.9 140.91 214.62 C133.21 207.34 128.38 197.68 126.42 185.64 C112.7 199.64 100.73 209.79 90.51 216.09 C80.29 222.39 69.02 225.54 56.7 225.54 M88.2 199.5 C94.36 199.5 100.31 197.96 106.05 194.88 C111.79 191.8 118.3 186.76 125.58 179.76 L126.0 94.5 L109.2 100.8 C94.92 106.12 84.0 111.51 76.44 116.97 C68.88 122.43 63.63 128.52 60.69 135.24 C57.75 141.96 56.28 149.66 56.28 158.34 C56.28 170.38 59.29 180.25 65.31 187.95 C71.33 195.65 78.96 199.5 88.2 199.5 M126.67 88.29 L159.68 76.44 L159.68 81.65 L126.67 93.49 L126.67 88.29 Z";

export const BrandA = ({ fill = "#F1E9D6", className = "" }) => (
  <svg viewBox="0 0 225.12 225.54" className={`block w-full h-auto ${className}`} aria-hidden="true">
    <path d={A_PATH} fill={fill} fillRule="evenodd" />
  </svg>
);

/**
 * The a° mark — exact vector artwork from the brand logo PDF.
 * Ring size/position measured from the uploaded vector file.
 */
export const HeroLogo = ({ fontSize = "clamp(7rem, 26vw, 10rem)", showSteam = true, className = "" }) => {
  return (
    <div
      className={`relative inline-block select-none ${className}`}
      style={{ width: fontSize, transform: "translateX(-17.9%)" }}
      data-testid="hero-logo"
    >
      <BrandA />

      {/* Degree symbol — positioned per the brand logo */}
      <span
        className="absolute block"
        style={{ left: "98%", top: "-7.9%", width: "39.7%", height: "39.7%" }}
      >
        <DegreeRing size="100%" display="block" withSteam={showSteam} />
      </span>
    </div>
  );
};

/** Compact a° for navbar / footer */
export const MiniMark = ({ className = "" }) => (
  <span className={`relative inline-block ${className}`} aria-label="house of appetite" style={{ width: "2rem" }}>
    <span className="relative block" style={{ width: "72%" }}>
      <BrandA />
      <span
        className="absolute rounded-full border-2"
        style={{ left: "97%", top: "-8%", width: "40%", height: "40%", borderColor: "var(--hoa-gold-500)" }}
      />
    </span>
  </span>
);
